import { ButtonGroup, HighLowChart } from '@toggle/design-system';
import { useElemSize } from '@toggle/helpers';
import { Entity } from '@toggle/toggle';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useGetEarningById } from '~/hooks/use-get-earning-by-id/useGetEarningById';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useScenarios } from '~/hooks/use-scenarios/useScenarios';
import { TabsType } from '~/modules/tab-content/types';
import { ChartLegend } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/components/earnings-chart/components/chart-legend/ChartLegend';
import { Expectation } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/types';

import * as S from './EpsExpectationsSection.styles';

interface EpsExpectationsSectionProps {
  earningsId: string;
  entity: Entity;
}

const CHART_HEIGHT = 300;
const START_HORIZON = '-2d';
const END_HORIZON = '5d';

export const EpsExpectationsSection = ({
  earningsId,
  entity,
}: EpsExpectationsSectionProps) => {
  const { t } = useTranslation(['earnings']);
  const chartWrapperRef = useRef(null);
  const { width } = useElemSize(chartWrapperRef);
  const [expectation, setExpectation] = useState<Expectation>(Expectation.Beat);
  const { navigateToPage } = useNavigateToAssetOverview();
  const [loading, setLoading] = useState(false);

  const {
    data: earningsData,
    isError: isEarningsDataError,
    error: earningsDataError,
  } = useGetEarningById(earningsId);

  const {
    sendMessage,
    data: scenariosData,
    isLoading: isScenariosLoading,
  } = useScenarios();

  const hasError = isEarningsDataError;
  const errorMessage = earningsDataError?.message;

  const buttonOptions = useMemo(
    () => [
      {
        label: t('earnings:earningsInsight.drawer.earningsPreview.beats'),
        value: Expectation.Beat,
      },
      {
        label: t('earnings:earningsInsight.drawer.earningsPreview.misses'),
        value: Expectation.Miss,
      },
    ],
    [t]
  );

  const searchParams = useMemo(() => {
    const params = new URLSearchParams();

    if (earningsData?.eps?.surprise !== undefined && expectation) {
      params.append(
        'epsFilter',
        expectation === Expectation.Beat ? 'beats' : 'misses'
      );
      params.append('epsValue', earningsData.eps.surprise.toString());
    } else {
      params.append(
        'epsFilter',
        expectation === Expectation.Beat ? 'beats' : 'misses'
      );
    }

    return params;
  }, [earningsData, expectation]);

  const mappedScenariosData = useMemo(() => {
    if (scenariosData.length === 0) {
      return [];
    }

    const firstScenario = scenariosData[0];

    return (
      firstScenario?.predictions?.map(item => ({
        high: item.high,
        median: item.median,
        low: item.low,
        label: t(item.horizon),
      })) ?? []
    );
  }, [scenariosData]);

  useEffect(() => {
    if (!earningsData || !expectation) {
      return;
    }

    const condition = expectation === Expectation.Beat ? 'above' : 'below';
    const value = expectation === Expectation.Beat ? 0.005 : -0.005;

    const payload = {
      entities: [
        {
          tag: entity.tag,
        },
      ],
      conditions: [
        {
          condition: 'above',
          snake: `${entity.tag}.event_earnings_release`,
          value: 0.5,
        },
        {
          condition: condition,
          snake: `${entity.tag}.surprise_absolute_eps`,
          value: value,
        },
      ],
      start_horizon: START_HORIZON,
      end_horizon: END_HORIZON,
      skip_report: true,
      non_overlapping: true,
    };

    sendMessage(payload);

    const timeoutId = window.setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [earningsData, entity.tag, expectation]);

  const handleExpectationChange = useCallback(
    (index: number) => {
      setExpectation(buttonOptions[index].value);
      setLoading(true);
    },
    [buttonOptions]
  );

  const handleDigIntoData = useCallback(() => {
    navigateToPage({
      entityTag: entity.tag,
      tab: TabsType.Earnings,
      searchParams,
    });
  }, [navigateToPage, entity.tag, searchParams]);

  const entityName = entity.name_short || entity.name;
  const activeButtonLabel =
    buttonOptions
      .find(option => option.value === expectation)
      ?.label?.toLowerCase() ?? '';

  const shouldShowLoader =
    (isScenariosLoading && mappedScenariosData.length === 0) || loading;

  return (
    <S.Root>
      <S.ButtonGroupWrapper>
        <S.TitleLabel>
          {t('earnings:earningsInsight.drawer.earningsPreview.whatIfEps')}
        </S.TitleLabel>
        <S.StyledButtonGroup>
          <ButtonGroup
            activeIndex={buttonOptions.findIndex(
              option => option.value === expectation
            )}
            labels={buttonOptions.map(option => option.label)}
            onClick={handleExpectationChange}
          />
        </S.StyledButtonGroup>
        <S.TitleLabel>
          {t('earnings:earningsInsight.drawer.earningsPreview.expectations')}
        </S.TitleLabel>
      </S.ButtonGroupWrapper>
      {hasError && errorMessage && (
        <div data-testid="error-message">{errorMessage}</div>
      )}
      {!shouldShowLoader && mappedScenariosData.length > 0 ? (
        <>
          <S.FlexRow>
            <S.ChartTitleLabel>
              {t(
                'earnings:earningsInsight.drawer.earningsPreview.stockPerformanceAfterEarnings',
                {
                  name: entityName,
                  count: scenariosData[0]?.episodes?.length,
                  expectation: activeButtonLabel,
                }
              )}
            </S.ChartTitleLabel>
            <S.StyledButton
              label={t(
                'earnings:earningsInsight.drawer.earningsPreview.digIntoTheData'
              )}
              variant="empty"
              onClick={handleDigIntoData}
            />
          </S.FlexRow>
          <ChartLegend />
          <S.ChartWrapper ref={chartWrapperRef} data-testid="chart-container">
            <HighLowChart
              data={mappedScenariosData}
              width={width}
              height={CHART_HEIGHT}
              variant="secondary"
            />
          </S.ChartWrapper>
        </>
      ) : (
        <S.StyledLoader />
      )}
    </S.Root>
  );
};
